import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from './Services';
import { Search, ArrowUpRight, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import CtaBanner from '../components/CtaBanner';
import { fetchBlogTags, fetchBlogPosts, fetchFeaturedPosts } from '../api/blog';
import { buildBreadcrumbLd } from '../lib/structuredData';

const PAGE_SIZE = 12;
const SITE_URL = 'https://residentialsupport.co.uk';

const Blog = () => {
  const [tag, setTag] = useState('All');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState(''); // debounced
  const [page, setPage] = useState(1);

  const [tags, setTags] = useState(['All']);
  const [featured, setFeatured] = useState([]);
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const gridTopRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setPage(1);
  }, [tag, q]);

  // One-shot fetches: tags + featured
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tagsRes, featuredRes] = await Promise.all([fetchBlogTags(), fetchFeaturedPosts(3)]);
        if (cancelled) return;
        setTags(['All', ...tagsRes.tags.map((t) => t.tag)]);
        setFeatured(featuredRes.posts || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Per-page fetch
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchBlogPosts({ tag, q, page, limit: PAGE_SIZE, signal: controller.signal })
      .then((d) => {
        setPosts(d.posts || []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 1);
        setLoading(false);
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setLoading(false);
      });
    return () => controller.abort();
  }, [tag, q, page]);

  const goToPage = (next) => {
    const clamped = Math.min(Math.max(1, next), totalPages);
    setPage(clamped);
    if (gridTopRef.current) {
      const top = gridTopRef.current.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const pageNumbers = useMemo(() => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (page > 3) pages.push('…');
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
    return pages;
  }, [page, totalPages]);

  const heroPost = featured[0];
  const heroAside = featured.slice(1, 3);

  return (
    <div>
      <Helmet>
        <title>Blog · Residential Support — UK lettings insights</title>
        <meta
          name="description"
          content="Practical guides for UK letting agents and landlords on compliance, inventories, inspections, deposits, disputes and operations — written by the team who attend the properties."
        />
        <link rel="canonical" href={`${SITE_URL}/blog`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Residential Support Blog — UK lettings insights" />
        <meta
          property="og:description"
          content="Compliance, inventories, inspections, deposits and operations — practical writing for UK letting agents."
        />
        <meta property="og:url" content={`${SITE_URL}/blog`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(
            buildBreadcrumbLd([
              { label: 'Home', path: '/' },
              { label: 'Blog', path: '/blog' },
            ])
          )}
        </script>
      </Helmet>

      <PageHeader
        eyebrow="Blog"
        title="Insights from the UK rental front line."
        sub="Practical guides for letting agents and landlords — written by the team who actually attend the properties."
      />

      {/* Featured */}
      {heroPost && (
        <section className="pt-12 pb-6 bg-[var(--rs-cream)]">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-8 items-stretch">
              <Link
                to={`/blog/${heroPost.id}`}
                className="lg:col-span-7 relative overflow-hidden rounded-[24px] group block"
                data-testid="blog-featured-hero"
              >
                <img src={heroPost.image} alt={heroPost.title} className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--rs-ink)]/95 via-[var(--rs-ink)]/70 to-[var(--rs-ink)]/15" />
                <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-9 text-[var(--rs-cream)]">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2.5 py-1 rounded-full bg-[var(--rs-coral)] text-white font-semibold">{heroPost.tag}</span>
                    <span className="opacity-90">{heroPost.readTime}</span>
                  </div>
                  <h2 className="h-display text-2xl md:text-3xl mt-3 max-w-2xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">{heroPost.title}</h2>
                  <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-[var(--rs-coral)] group-hover:gap-2 transition-all">
                    Read article <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
              <div className="lg:col-span-5 grid gap-4">
                {heroAside.map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.id}`}
                    className="rs-card overflow-hidden lift grid grid-cols-5 group"
                    data-testid={`blog-featured-aside-${p.id}`}
                  >
                    <div className="col-span-2 overflow-hidden">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="col-span-3 p-5">
                      <div className="flex items-center gap-2 text-xs text-[var(--rs-ink)]/55">
                        <span className="px-2 py-0.5 rounded-full bg-[var(--rs-sand)] text-[var(--rs-ink)]">{p.tag}</span>
                        <span>{p.readTime}</span>
                      </div>
                      <h3 className="h-display text-lg text-[var(--rs-ink)] mt-2 leading-snug">{p.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section id="all" className="sect bg-[var(--rs-cream)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div ref={gridTopRef} className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-10">
            <div className="flex flex-wrap gap-2" data-testid="blog-tag-filters">
              {tags.map((t) => (
                <button
                  key={t}
                  onClick={() => setTag(t)}
                  data-testid={`blog-tag-${t.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${tag === t ? 'bg-[var(--rs-ink)] text-[var(--rs-cream)]' : 'bg-white border border-[#E7E0D2] text-[var(--rs-ink)] hover:bg-[var(--rs-sand)]'}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="relative max-w-xs w-full">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rs-ink)]/40" />
              <Input
                placeholder="Search articles…"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                className="pl-9 bg-white border-[#E7E0D2]"
                data-testid="blog-search-input"
              />
            </div>
          </div>

          {error && (
            <p className="text-center text-red-600 py-10" data-testid="blog-error">
              Could not load articles. Please try again shortly.
            </p>
          )}

          {loading ? (
            <div className="py-20 flex items-center justify-center text-[var(--rs-ink)]/55" data-testid="blog-loading">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading articles…
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-[var(--rs-ink)]/55 py-16" data-testid="blog-empty-state">
              Nothing matches that search yet.
            </p>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-grid">
                {posts.map((p) => (
                  <Link key={p.id} to={`/blog/${p.id}`} className="rs-card overflow-hidden lift group block" data-testid={`blog-card-${p.id}`}>
                    <div className="h-52 overflow-hidden">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-xs text-[var(--rs-ink)]/55">
                        <span className="px-2.5 py-1 rounded-full bg-[var(--rs-sand)] text-[var(--rs-ink)] font-medium">{p.tag}</span>
                        <span>{p.readTime}</span>
                      </div>
                      <h3 className="h-display text-xl text-[var(--rs-ink)] mt-3 leading-snug">{p.title}</h3>
                      <p className="text-sm text-[var(--rs-ink)]/70 mt-2">{p.excerpt}</p>
                      <div className="mt-5 flex items-center justify-end">
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--rs-coral)] group-hover:gap-2 transition-all">
                          Read <ArrowUpRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="blog-pagination">
                  <p className="text-sm text-[var(--rs-ink)]/60" data-testid="blog-pagination-summary">
                    Showing{' '}
                    <span className="font-semibold text-[var(--rs-ink)]">{(page - 1) * PAGE_SIZE + 1}</span>–
                    <span className="font-semibold text-[var(--rs-ink)]">{Math.min(page * PAGE_SIZE, total)}</span> of{' '}
                    <span className="font-semibold text-[var(--rs-ink)]">{total}</span> articles
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      data-testid="blog-pagination-prev"
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm bg-white border border-[#E7E0D2] text-[var(--rs-ink)] hover:bg-[var(--rs-sand)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="w-4 h-4" /> Prev
                    </button>
                    {pageNumbers.map((n, idx) =>
                      n === '…' ? (
                        <span key={`e-${idx}`} className="px-2 text-[var(--rs-ink)]/50 select-none">…</span>
                      ) : (
                        <button
                          key={n}
                          onClick={() => goToPage(n)}
                          data-testid={`blog-pagination-page-${n}`}
                          aria-current={page === n ? 'page' : undefined}
                          className={`min-w-[40px] px-3 py-2 rounded-full text-sm transition-colors ${
                            page === n
                              ? 'bg-[var(--rs-ink)] text-[var(--rs-cream)]'
                              : 'bg-white border border-[#E7E0D2] text-[var(--rs-ink)] hover:bg-[var(--rs-sand)]'
                          }`}
                        >
                          {n}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      data-testid="blog-pagination-next"
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm bg-white border border-[#E7E0D2] text-[var(--rs-ink)] hover:bg-[var(--rs-sand)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label="Next page"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <CtaBanner />
    </div>
  );
};

export default Blog;
